import React, { useMemo, useState } from 'react';
import { Row, Col, Card, Button, Space, Typography, Select, DatePicker, Table, Tag, Input, Tabs } from 'antd';
import { DownloadOutlined, FilePdfOutlined, FileExcelOutlined, ScheduleOutlined, BarChartOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const WhiteLabelReports = () => {
  const [client, setClient] = useState('all');
  const [reportType, setReportType] = useState('campaign-performance');
  const [format, setFormat] = useState('csv');
  const [search, setSearch] = useState('');

  const clients = ['all', 'Acme Co', 'Northwind', 'Contoso', 'AdventureWorks'];
  const reportTypes = [
    { value: 'campaign-performance', label: 'Campaign Performance' },
    { value: 'client-summary', label: 'Client Summary' },
    { value: 'billing-summary', label: 'Billing Summary' },
    { value: 'traffic-impressions', label: 'Traffic & Impressions' },
  ];

  const recentReports = useMemo(() => ([
    { id: 'rpt_105', name: 'Campaign Performance - Acme Co', client: 'Acme Co', type: 'Campaign Performance', created: '2025-08-25 10:10', status: 'Ready', format: 'CSV' },
    { id: 'rpt_104', name: 'Client Summary - Northwind', client: 'Northwind', type: 'Client Summary', created: '2025-08-24 09:30', status: 'Ready', format: 'PDF' },
    { id: 'rpt_103', name: 'Billing Summary - All', client: 'All', type: 'Billing Summary', created: '2025-08-20 14:05', status: 'Ready', format: 'CSV' },
    { id: 'rpt_102', name: 'Traffic & Impressions - Contoso', client: 'Contoso', type: 'Traffic & Impressions', created: '2025-08-18 16:40', status: 'Processing', format: 'PDF' },
  ]), []);

  const filteredReports = useMemo(() => (
    recentReports.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()))
  ), [recentReports, search]);

  const columns = [
    { title: 'Report', dataIndex: 'name', key: 'name' },
    { title: 'Client', dataIndex: 'client', key: 'client', width: 160 },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 200 },
    { title: 'Created', dataIndex: 'created', key: 'created', width: 180 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: s => <Tag color={s === 'Ready' ? 'green' : 'gold'}>{s}</Tag> },
    { title: 'Format', dataIndex: 'format', key: 'format', width: 100 },
    { title: 'Actions', key: 'actions', width: 160, render: (_, r) => (
      <Space>
        <Button icon={<DownloadOutlined />} size="small" disabled={r.status !== 'Ready'}>Download</Button>
        <Button type="link" size="small">Details</Button>
      </Space>
    )},
  ];

  const schedules = [
    { id: 'sch_1', name: 'Weekly Client Summary (All)', cadence: 'Weekly - Mondays 9:00', recipients: 'ops@agency.com', active: true },
    { id: 'sch_2', name: 'Monthly Billing (Finance)', cadence: 'Monthly - 1st 8:00', recipients: 'finance@agency.com', active: true },
  ];

  const scheduleList = (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }}>
        {schedules.map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <ScheduleOutlined />
              <div>
                <Text strong>{s.name}</Text>
                <div><Text type="secondary">{s.cadence} • {s.recipients}</Text></div>
              </div>
            </Space>
            <Space>
              {s.active ? <Tag color="blue">Active</Tag> : <Tag>Paused</Tag>}
              <Button size="small" type="link">Edit</Button>
              <Button size="small" type="link">Pause</Button>
            </Space>
          </div>
        ))}
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Reports Export</Title>
          <Text type="secondary">Export CSV/PDF reports across clients with reusable schedules.</Text>
        </div>

        {/* Toolbar */}
        <Card bodyStyle={{ padding: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} lg={5}>
              <Select value={client} onChange={setClient} style={{ width: '100%' }}>
                {clients.map(c => <Select.Option key={c} value={c}>{c === 'all' ? 'All Clients' : c}</Select.Option>)}
              </Select>
            </Col>
            <Col xs={24} lg={7}>
              <RangePicker style={{ width: '100%' }} allowEmpty={[true, true]} />
            </Col>
            <Col xs={24} lg={5}>
              <Select value={reportType} onChange={setReportType} style={{ width: '100%' }} options={reportTypes} />
            </Col>
            <Col xs={24} lg={3}>
              <Select value={format} onChange={setFormat} style={{ width: '100%' }} options={[{ value: 'csv', label: 'CSV' }, { value: 'pdf', label: 'PDF' }]} />
            </Col>
            <Col xs={24} lg={4}>
              <Input placeholder="Search reports" value={search} onChange={(e) => setSearch(e.target.value)} />
            </Col>
            <Col xs={24} lg={0} flex="auto" />
            <Col xs={24} lg={0} style={{ textAlign: 'right' }}>
              {/* spacer for responsiveness */}
            </Col>
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={0} />
            <Col xs={24} lg={4} style={{ textAlign: 'right' }}>
              <Space>
                <Button icon={<FileExcelOutlined />}>Export</Button>
                <Button type="primary" icon={format === 'pdf' ? <FilePdfOutlined /> : <DownloadOutlined />}>Generate</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Recent Reports" bodyStyle={{ padding: 0 }} extra={<Button type="link" icon={<BarChartOutlined />}>Analytics</Button>}>
              <Table rowKey="id" columns={columns} dataSource={filteredReports} pagination={{ pageSize: 8 }} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Tabs
              items={[
                { key: 'schedules', label: 'Schedules', children: scheduleList },
                { key: 'placeholders', label: 'Placeholders', children: (
                  <Card>
                    <Space direction="vertical">
                      <Text type="secondary">Coming soon: parameterized placeholders for dynamic report names and filters.</Text>
                    </Space>
                  </Card>
                ) },
              ]}
            />
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default WhiteLabelReports;
